import { supabase } from "@/integrations/supabase/client";
import { safeLocalStorage, safeGetItem, safeSetItem, safeRemoveItem } from "@/lib/safe-storage";

export interface UserProfile {
  user_id: string;
  email: string;
  role: 'student' | 'mentor' | 'admin';
  is_active: boolean;
  first_name?: string;
  last_name?: string;
}

export const cleanupAuthState = () => {
  try {
    // Remove all Supabase auth keys from localStorage (safely)
    try {
      const ls = safeLocalStorage;
      for (let i = ls.length - 1; i >= 0; i--) {
        const key = ls.key(i);
        if (!key) continue;
        if (key.startsWith('supabase.auth.') || key.includes('sb-') || key.startsWith('sb:auth:')) {
          try {
            ls.removeItem(key);
          } catch (error) {
            console.warn('Failed to remove localStorage key:', key, error);
          }
        }
      }
    } catch (e) {
      console.warn('Safe localStorage cleanup failed:', e);
    }
    
    // Remove from sessionStorage if in use
    if (typeof sessionStorage !== 'undefined') {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-') || key.startsWith('sb:auth:')) {
          try {
            sessionStorage.removeItem(key);
          } catch (error) {
            console.warn('Failed to remove sessionStorage key:', key, error);
          }
        }
      });
    }
  } catch (error) {
    console.warn('Auth state cleanup failed:', error);
  }
};

export const signOutGlobally = async () => {
  try {
    cleanupAuthState();
    await supabase.auth.signOut({ scope: 'global' });
  } catch (error) {
    // Continue even if this fails
    console.warn('Global sign out failed:', error);
  }
};

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('user_id, email, role, is_active, first_name, last_name')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) throw error;
    return data as UserProfile;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

export const getCorrectDashboardRoute = (role: string): string => {
  switch (role) {
    case 'admin':
      return '/dashboard/admin';
    case 'mentor':
      return '/dashboard/mentor';
    case 'student':
      return '/dashboard/student';
    default:
      return '/';
  }
};

export const redirectToCorrectDashboard = async (userId: string) => {
  // Check for post-login redirect first
  const redirectPath = safeGetItem('redirectAfterLogin');
  if (redirectPath) {
    safeRemoveItem('redirectAfterLogin');
    window.location.href = redirectPath;
    return;
  }

  const profile = await getUserProfile(userId);
  if (profile) {
    const route = getCorrectDashboardRoute(profile.role);
    window.location.href = route;
  } else {
    // If no profile found, sign out and redirect to home
    await signOutGlobally();
    window.location.href = '/';
  }
};

export const reactivateUserIfNeeded = async (userId: string, isActive: boolean) => {
  if (isActive === false) {
    try {
      await supabase.rpc('reactivate_user_account', { p_user_id: userId });
      return true; // Account was reactivated
    } catch (error) {
      console.error('Error reactivating account:', error);
      return false;
    }
  }
  return false; // No reactivation needed
};

export const handleBookingContinuation = () => {
  // Check if user was in the middle of booking
  const selectedMentorId = safeGetItem('selectedMentorId');
  const bookingTimeSlot = safeGetItem('bookingTimeSlot');
  const bookingNotes = safeGetItem('bookingNotes');
  
  if (selectedMentorId) {
    // Return booking data to continue the flow
    return {
      selectedMentorId,
      bookingTimeSlot,
      bookingNotes,
    };
  }
  
  return null;
};

export const handlePostLoginRedirect = () => {
  const redirectPath = safeGetItem('redirectAfterLogin');
  if (redirectPath) {
    safeRemoveItem('redirectAfterLogin');
    return redirectPath;
  }
  return null;
};

export const clearBookingData = () => {
  safeRemoveItem('selectedMentorId');
  safeRemoveItem('bookingTimeSlot');
  safeRemoveItem('bookingNotes');
};

export const storeBookingData = (mentorId: string, timeSlot?: string, notes?: string) => {
  safeSetItem('selectedMentorId', mentorId);
  if (timeSlot) safeSetItem('bookingTimeSlot', timeSlot);
  if (notes) safeSetItem('bookingNotes', notes);
};